import i18n from '@/utils/i18n';
import type { FacetOption } from './ContainerFacetChip';
import type { AdminContainerTypeFilter } from '../../../graphql/generatedTypes';
import { SEGMENT_VALUES } from './constants';

/**
 * Filter model shared by the desktop filter bar / table and the mobile card
 * list. Class / Group / Segment / Country are always-promoted facets; Size /
 * Branding / Legal owner appear once added via the "Add filter" control.
 * Filtering itself happens server-side — see toGqlContainerTypeFilter.
 */

export type FacetKey =
  | 'class'
  | 'group'
  | 'segment'
  | 'countries'
  | 'size'
  | 'branding'
  | 'legalOwner';

export const CORE_FACETS: FacetKey[] = [
  'class',
  'group',
  'segment',
  'countries',
];
export const EXTRA_FACETS: FacetKey[] = ['size', 'branding', 'legalOwner'];

export type FacetSelection = Partial<Record<FacetKey, string[]>>;

export type ContainerTypeFilterState = {
  search: string;
  showArchived: boolean;
  facets: FacetSelection;
  /** Extra facets currently shown as chips (in the order they were added). */
  extraFacets: FacetKey[];
};

export const EMPTY_FILTER_STATE: ContainerTypeFilterState = {
  search: '',
  showArchived: false,
  facets: {},
  extraFacets: [],
};

export const facetLabel = (key: FacetKey): string =>
  ({
    class: i18n.t('EditContainerInfo:colClass'),
    group: i18n.t('EditContainerInfo:colGroup'),
    segment: i18n.t('EditContainerInfo:segment'),
    countries: i18n.t('Common:country'),
    size: i18n.t('EditContainerInfo:colSize'),
    branding: i18n.t('EditContainerInfo:colBranding'),
    legalOwner: i18n.t('EditContainerInfo:colLegalOwner'),
  })[key];

export type FacetOptionContext = {
  containerClasses: FacetOption[];
  groupOptions: FacetOption[];
  countries: FacetOption[];
  /** Distinct values from the server facets query. */
  sizes: string[];
  brandings: string[];
  legalOwners: string[];
};

export function buildFacetOptions(
  key: FacetKey,
  ctx: FacetOptionContext,
): FacetOption[] {
  const asOptions = (values: string[]) =>
    values.map((value) => ({ value, label: value }));
  switch (key) {
    case 'class':
      return ctx.containerClasses;
    case 'group':
      return ctx.groupOptions;
    case 'segment':
      return SEGMENT_VALUES.map((value) => ({
        value,
        label: i18n.t(`EditContainerInfo:segment${value}`),
      }));
    case 'countries':
      return ctx.countries;
    case 'size':
      return asOptions(ctx.sizes);
    case 'branding':
      return asOptions(ctx.brandings);
    case 'legalOwner':
      return asOptions(ctx.legalOwners);
    default:
      return [];
  }
}

export function hasAnyFacetValues(filters: ContainerTypeFilterState): boolean {
  return Object.values(filters.facets).some((values) => values?.length);
}

/** Map the UI filter state to the server-side AdminContainerTypeFilter. */
export function toGqlContainerTypeFilter(
  filters: ContainerTypeFilterState,
  search: string,
): AdminContainerTypeFilter {
  const values = (key: FacetKey) => {
    const selected = filters.facets[key];
    return selected?.length ? selected : null;
  };
  const trimmed = search.trim();
  return {
    search: trimmed === '' ? null : trimmed,
    archived: filters.showArchived,
    classes: values('class'),
    group_ids: values('group')?.map((id) => parseInt(id, 10)) ?? null,
    segments: values('segment'),
    country_codes: values('countries'),
    sizes: values('size'),
    brandings: values('branding'),
    legal_owners: values('legalOwner'),
  };
}

/** Display order within a page (mirrors the server sort). */
export function sortForDisplay<T extends { orderKey: number; id: number }>(
  rows: T[],
): T[] {
  return [...rows].sort((a, b) =>
    a.orderKey !== b.orderKey ? b.orderKey - a.orderKey : a.id - b.id,
  );
}
